import React, { useEffect, useState } from "react";
import { Sparkles, Lock, Mail, KeyRound, Loader2, AlertCircle } from "lucide-react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation, Trans } from "react-i18next";
import Header from "../components/Header";
import {
  claimCampaignAndCheckout,
  foundingLockInStatus,
  foundingLockInRegisterAndCheckout,
} from "../lib/api";
import { track } from "../lib/analytics";
import {
  trackViewContent,
  trackAddToCart,
  trackInitiateCheckout,
} from "../lib/marketingPixels";
import { useAuth } from "../context/AuthContext";

/**
 * LockIn — Instagram-shareable landing page for the founding lock-in
 * promo. The first 100 paid members receive Founding Lifetime Pro
 * after one $100 Stripe payment.
 *
 * Design rationale
 * ─────────────────
 * • Mobile-first: every Instagram tap will be on a phone.
 * • Single CTA above the fold so the urgency counter + the price + the
 *   sign-up form all fit in one viewport — Instagram traffic bounces
 *   if it has to scroll to find the action.
 * • Existing free-account users can claim without making a new account.
 * • If the cap is reached, the form is replaced by a "All 100 spots
 *   claimed — but you can still get Pro at the regular rate" CTA so
 *   the traffic still converts.
 */

const LockIn = () => {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const { user } = useAuth();
  const navigate = useNavigate();

  // Counter state — re-fetched on mount and every 30s while the page
  // is visible so the "X of 100 left" reads accurately as people claim.
  const [status, setStatus] = useState(null);
  const [statusError, setStatusError] = useState(null);

  // Form state
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState(null);

  const canceled = params.get("canceled") === "1";

  useEffect(() => {
    track("lock_in_landing_viewed", { from: params.get("ref") || null });
    // TikTok/Taboola/GTM: ViewContent — they're viewing the offer.
    trackViewContent({
      content_type: "lock_in_landing",
      content_id: "founding_lock_in",
      value: 100,
      currency: "USD",
    });
    let cancelled = false;
    const refresh = async () => {
      try {
        const s = await foundingLockInStatus();
        if (!cancelled) setStatus(s);
      } catch (e) {
        if (!cancelled) setStatusError(t("lockIn.statusErrorLoad"));
      }
    };
    refresh();
    const interval = setInterval(refresh, 30000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    // Lifetime follows the same verified-account rule as recurring plans.
    // New visitors create an account first, verify their mailbox, then return
    // here as an authenticated buyer for card-required Stripe Checkout.
    if (!user) {
      navigate("/login?signup=1&next=%2Flock-in");
      return;
    }
    if (submitting) return;
    setFormError(null);
    if (!name.trim() || !email.trim() || !password.trim()) {
      setFormError(t("lockIn.errorFieldsRequired"));
      return;
    }
    if (password.length < 8) {
      setFormError(t("lockIn.errorPasswordLength"));
      return;
    }
    setSubmitting(true);
    track("lock_in_signup_started", { email_domain: email.split("@")[1] || null });
    // TikTok/Taboola/GTM: form submitted — fires AddToCart since this
    // is the user's first signal of purchase intent (lock-in flow
    // signup === Stripe checkout intent).
    trackAddToCart({
      content_id: "founding_lock_in",
      content_name: "Founding Lifetime Pro · $100 once",
      content_type: "lifetime_access",
      value: 100,
      currency: "USD",
      quantity: 1,
    });
    try {
      const origin = window.location.origin;
      const res = await foundingLockInRegisterAndCheckout({
        email: email.trim(),
        password,
        name: name.trim() || null,
        origin_url: origin,
        locale: undefined,
      });
      if (res?.url) {
        track("lock_in_checkout_redirect");
        trackInitiateCheckout({
          content_id: "founding_lock_in",
          value: 100,
          currency: "USD",
        });
        window.location.assign(res.url);
        return;
      }
      setFormError(t("lockIn.errorStripeUnreachable"));
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || t("lockIn.errorGeneric");
      track("lock_in_signup_failed", { reason: String(detail).slice(0, 80) });
      setFormError(detail);
    } finally {
      setSubmitting(false);
    }
  };

  const isFull = status?.is_full === true;
  const remaining = status?.remaining ?? null;

  const claimExistingAccount = async () => {
    if (submitting || isFull) return;
    setSubmitting(true);
    setFormError(null);
    try {
      const res = await claimCampaignAndCheckout({
        campaign: "founding",
        origin_url: window.location.origin,
      });
      if (res?.url) {
        trackInitiateCheckout({
          content_id: "founding_lock_in",
          value: 100,
          currency: "USD",
        });
        window.location.assign(res.url);
        return;
      }
      setFormError(t("lockIn.errorStripeUnreachable"));
    } catch (err) {
      setFormError(
        err?.response?.data?.detail || err?.message || t("lockIn.errorGeneric"),
      );
    } finally {
      setSubmitting(false);
    }
  };

  // Existing free-account users can become one of the first 100 too.
  if (user) {
    return (
      <div className="min-h-screen trubeaute-gradient">
        <Header />
        <div className="pt-32 px-6 max-w-xl mx-auto" data-testid="lock-in-already-authed">
          <div className="rounded-3xl bg-white/80 backdrop-blur border border-[#EADDD7] p-8 shadow-[0_24px_60px_-30px_rgba(155,106,108,0.35)]">
            <div className="flex items-center gap-2 mb-4">
              <Sparkles className="h-5 w-5 text-[#9B6A6C]" />
              <span className="text-xs uppercase tracking-[0.22em] text-[#9B6A6C]">
                {t("lockIn.kicker")}
              </span>
            </div>
            <h1 className="text-3xl font-serif text-[#2C2123] mb-3 leading-tight">
              {t("lockIn.authedHeading", { name: user.name?.split(" ")[0] || t("lockIn.authedFallbackName") })}
            </h1>
            <p className="text-[#5C4B51] mb-6 leading-relaxed">
              <Trans
                i18nKey="lockIn.authedBody"
                components={{ strong: <span className="font-medium text-[#2C2123]" /> }}
              />
            </p>
            {formError && (
              <p className="text-sm text-[#9B6A6C] mb-4">{formError}</p>
            )}
            <button
              onClick={claimExistingAccount}
              disabled={submitting || isFull}
              data-testid="lock-in-go-pricing"
              className="bg-[#9B6A6C] hover:bg-[#5D4050] text-white px-7 py-3 rounded-full font-medium text-sm transition-colors"
            >
              {submitting ? t("lockIn.submitting") : t("lockIn.authedCta")}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen trubeaute-gradient">
      <Header />
      <main
        className="pt-24 sm:pt-28 px-5 sm:px-8 max-w-2xl mx-auto pb-16"
        data-testid="lock-in-landing"
      >
        {/* Tagline */}
        <div className="flex items-center gap-2 mb-4">
          <Lock className="h-4 w-4 text-[#9B6A6C]" />
          <span className="text-[11px] uppercase tracking-[0.28em] text-[#9B6A6C]">
            {t("lockIn.kicker")}
          </span>
        </div>

        {/* Hero */}
        <h1 className="font-serif text-[#2C2123] leading-tight text-4xl sm:text-5xl mb-4">
          {t("lockIn.headlineBefore")} <span className="italic">{t("lockIn.headlinePrice")}</span> — <br className="hidden sm:block" />
          {t("lockIn.headlineAfter")}
        </h1>
        <p className="text-[#5C4B51] text-base sm:text-lg mb-7 leading-relaxed max-w-xl">
          <Trans
            i18nKey="lockIn.subline"
            components={{ em: <span className="font-medium text-[#2C2123]" /> }}
          />
        </p>

        {/* Counter */}
        {status && !isFull && (
          <div
            className="inline-flex items-center gap-2 bg-white/70 backdrop-blur border border-[#EADDD7] rounded-full px-4 py-2 mb-8"
            data-testid="lock-in-counter"
          >
            <span className="h-2 w-2 rounded-full bg-[#9B6A6C] animate-pulse" />
            <span className="text-sm text-[#2C2123] font-medium">
              <Trans
                i18nKey="lockIn.spotsLeft"
                values={{ remaining, limit: status.limit }}
                components={{ rem: <span data-testid="lock-in-remaining" /> }}
              />
            </span>
          </div>
        )}
        {statusError && (
          <p className="text-sm text-[#9B6A6C] mb-4">{statusError}</p>
        )}

        {/* Cap reached */}
        {isFull ? (
          <div
            className="rounded-3xl bg-white/85 border border-[#EADDD7] p-7 shadow-[0_24px_60px_-30px_rgba(155,106,108,0.35)]"
            data-testid="lock-in-full"
          >
            <h2 className="text-2xl font-serif text-[#2C2123] mb-3 leading-snug">
              {t("lockIn.capHeading")}
            </h2>
            <p className="text-[#5C4B51] mb-5 leading-relaxed">
              {t("lockIn.capBody")}
            </p>
            <button
              onClick={() => navigate("/pricing")}
              data-testid="lock-in-fallback-pricing"
              className="bg-[#9B6A6C] hover:bg-[#5D4050] text-white px-7 py-3 rounded-full font-medium text-sm transition-colors"
            >
              {t("lockIn.seePricing")}
            </button>
          </div>
        ) : (
          /* Sign-up form */
          <form
            onSubmit={submit}
            noValidate
            className="rounded-3xl bg-white/85 border border-[#EADDD7] p-6 sm:p-7 shadow-[0_24px_60px_-30px_rgba(155,106,108,0.35)]"
            data-testid="lock-in-form"
          >
            <h2 className="text-xl font-serif text-[#2C2123] mb-4">
              {t("lockIn.claimHeading")}
            </h2>

            {canceled && (
              <div className="bg-[#FBEDE9] border border-[#E4D2DC] rounded-xl p-3 mb-4 text-sm text-[#5D4050]">
                {t("lockIn.canceledNote")}
              </div>
            )}

            <label className="block mb-3">
              <span className="text-xs uppercase tracking-[0.22em] text-[#9B6A6C] mb-1.5 block">
                {t("lockIn.nameLabel")} <span className="text-[#C9A8A4] normal-case tracking-normal">{t("lockIn.nameOptional")}</span>
              </span>
              <input
                type="text"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={submitting}
                className="w-full rounded-xl border border-[#EADDD7] px-4 py-2.5 bg-white text-[#2C2123] focus:outline-none focus:border-[#9B6A6C] transition-colors"
                placeholder={t("lockIn.namePlaceholder")}
                data-testid="lock-in-name-input"
                maxLength={120}
              />
            </label>

            <label className="block mb-3">
              <span className="text-xs uppercase tracking-[0.22em] text-[#9B6A6C] mb-1.5 block">
                {t("lockIn.emailLabel")}
              </span>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#C9A8A4]" />
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={submitting}
                  className="w-full rounded-xl border border-[#EADDD7] pl-10 pr-4 py-2.5 bg-white text-[#2C2123] focus:outline-none focus:border-[#9B6A6C] transition-colors"
                  placeholder={t("lockIn.emailPlaceholder")}
                  data-testid="lock-in-email-input"
                  autoComplete="email"
                  maxLength={200}
                />
              </div>
            </label>

            <label className="block mb-5">
              <span className="text-xs uppercase tracking-[0.22em] text-[#9B6A6C] mb-1.5 block">
                {t("lockIn.passwordLabel")}
              </span>
              <div className="relative">
                <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#C9A8A4]" />
                <input
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={submitting}
                  maxLength={200}
                  className="w-full rounded-xl border border-[#EADDD7] pl-10 pr-4 py-2.5 bg-white text-[#2C2123] focus:outline-none focus:border-[#9B6A6C] transition-colors"
                  placeholder={t("lockIn.passwordPlaceholder")}
                  data-testid="lock-in-password-input"
                  autoComplete="new-password"
                />
              </div>
            </label>

            {formError && (
              <div
                className="flex items-start gap-2 text-sm text-[#9B6A6C] mb-4 bg-[#FBEDE9] border border-[#E4D2DC] rounded-xl p-3"
                data-testid="lock-in-form-error"
              >
                <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                <span>{formError}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              data-testid="lock-in-submit"
              className="w-full bg-[#9B6A6C] hover:bg-[#5D4050] disabled:opacity-60 disabled:cursor-not-allowed text-white px-6 py-3.5 rounded-full font-medium text-sm transition-colors flex items-center justify-center gap-2"
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {t("lockIn.submitting")}
                </>
              ) : (
                <>
                  <Lock className="h-4 w-4" />
                  {t("lockIn.submit")}
                </>
              )}
            </button>

            <p className="text-[11px] text-[#9B6A6C] text-center mt-4 leading-relaxed">
              <Trans
                i18nKey="lockIn.footerLegal"
                components={{
                  terms: <Link to="/terms" className="underline" />,
                  privacy: <Link to="/privacy" className="underline" />,
                }}
              />
            </p>
          </form>
        )}

        {/* What this offer is */}
        <div className="mt-10 pt-8 border-t border-[#EADDD7]">
          <h3 className="text-xs uppercase tracking-[0.22em] text-[#9B6A6C] mb-3">
            {t("lockIn.howTitle")}
          </h3>
          <ol className="space-y-3 text-[#5C4B51] text-sm leading-relaxed">
            <li><span className="text-[#9B6A6C] font-medium">1.</span> {t("lockIn.how1")}</li>
            <li><span className="text-[#9B6A6C] font-medium">2.</span> {t("lockIn.how2")}</li>
            <li><span className="text-[#9B6A6C] font-medium">3.</span> {t("lockIn.how3")}</li>
          </ol>
        </div>
      </main>
    </div>
  );
};

export default LockIn;
